import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity } from "lucide-react";
import {
  getOrganizationActivity,
  AuditEventItem,
} from "@/services/organizationsApi";

// Date relative FR (même approche que scraping-stats/page.tsx, dupliquée
// localement faute de helper partagé dans lib/).
function formatRelativeFr(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffSec = Math.round((d.getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat("fr", { numeric: "auto" });
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(Math.round(diffSec), "second");
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), "hour");
  return rtf.format(Math.round(diffSec / 86400), "day");
}

export function B2bActivityFeedCard() {
  const navigate = useNavigate();
  const [items, setItems] = useState<AuditEventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getOrganizationActivity(navigate, { limit: 15 });
        setItems(res.items);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur inconnue");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">
          Activité B2B récente
        </CardTitle>
        <Activity className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="text-sm text-muted-foreground">Chargement...</div>
        )}
        {error && <div className="text-sm text-red-600">{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div className="text-sm text-muted-foreground">
            Aucune activité récente
          </div>
        )}
        {!loading && !error && items.length > 0 && (
          <ul className="divide-y text-sm">
            {items.map((it) => (
              <li
                key={it.id}
                className="flex items-center justify-between gap-3 py-2"
              >
                <div className="min-w-0">
                  <span className="font-mono text-xs">{it.action}</span>
                  {it.organization_name && (
                    <span className="ml-2 text-muted-foreground">
                      {it.organization_name}
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground">
                  {it.actor_email && (
                    <span className="truncate max-w-[160px]">
                      {it.actor_email}
                    </span>
                  )}
                  <span>{formatRelativeFr(it.created_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
